import { AppThunk } from '../../../app/store';
import { CartItemAdditionTypes } from '../../../constants/event';
import { cartActions } from '../../../reducers/cartSlice';
import { CartItem } from '../../../utils/cart';
import logger from '../../../utils/logger';
import { draftCartActions } from './draftCart.slice';

/**
 * HITL-1614 — copy one AI cart line into My Cart.
 *
 * The only writer into `draftCart` in this story. It reads `cartSlice` but never
 * writes it: the AI's cart is untouched by a copy, which is what makes this safe
 * to run in `Shadow`.
 *
 * Three things this has to get right:
 *
 * 1. **A deep copy, not a reference.** Both slices run through Immer, and a
 *    shared nested `childModifierGroups` object would let a draft edit reach
 *    into the order. `CartItem` is plain JSON in Redux, so a structural clone is
 *    exact.
 * 2. **A fresh `cartItemId` from the DRAFT's own `sequenceId`.** The two slices
 *    number independently and both start at 1; reusing the source id would
 *    collide the moment a second item is copied.
 * 3. **`addedBy` is preserved.** The line really did come from the AI and saying
 *    so is useful in My Cart. It also keeps `addItemToCart`'s upsell handlers
 *    off the draft, which replay session-level effects that have no business
 *    firing for a copy.
 */
export const copyCartItemToDraft =
  (cartItem: CartItem): AppThunk =>
  (dispatch, getState) => {
    if (!cartItem) return;

    const { sequenceId } = getState().draftCart;

    // JSON round-trip rather than `structuredClone`: `CartItem` is plain data,
    // and RTK freezes state in development, so a shallow spread would leave the
    // nested modifier tree shared with `cartSlice`.
    const copy: CartItem = JSON.parse(JSON.stringify(cartItem));
    copy.cartItemId = sequenceId;

    // An auto-combined line is a COMBO assembled from other cart lines and its
    // `autoCombinedCartItemIds` point into `cartSlice`'s id space. Those ids
    // mean nothing here, so the copy is a plain item. The control is not offered
    // on such a line either (see `CartItemReview`); this is the belt to that
    // brace.
    delete copy.autoCombinedCartItemIds;
    delete copy.isAutoCombined;

    dispatch(draftCartActions.addItemToCart({ cartItem: copy }));

    logger.debug({
      message: 'Copied an AI cart line into My Cart',
      moreInfo: `${cartItem.name} -> draft cartItemId ${sequenceId}`,
    });
  };

/**
 * HITL-1615 — write one draft line into the order.
 *
 * Shared by both promote paths. Three things it has to get right:
 *
 * 1. **A fresh id from `cartSlice`'s own `sequenceId`.** The two slices number
 *    independently and both start at 1.
 * 2. **`addedBy: human`.** This is not cosmetic. `useHypothesisFrames` disarms
 *    its intercept-import on the agent's first real edit, and that check is
 *    `addedBy !== AI` (authorship) OR a baseline key gone/changed (structural).
 *    An APPENDED line leaves every baseline key intact, so if the promoted item
 *    still said `addedBy: ai` neither branch would trip and the AI's marked
 *    `handoff_flush` frame could import straight over the agent's work. It is
 *    also simply true: the agent authored this line into the order.
 * 3. **Quantity carried over.** `addItemToCart` always sets 1.
 */
const writeLineToOrder =
  (cartItem: CartItem, quantity: number): AppThunk<number> =>
  (dispatch, getState) => {
    const { sequenceId, cartItems } = getState().cart;
    // `addItemToCart` keys on `cartItem.cartItemId` and overwrites whatever is
    // there, so an id that is already taken silently DESTROYS a line of the live
    // order — in `Intercept`, that is a POS-visible loss. `sequenceId` should
    // always be past the last id, but it is maintained by a different slice than
    // the one this item came from, so the max is taken rather than trusted.
    const takenIds = Object.keys(cartItems ?? {}).map(Number);
    const cartItemId = Math.max(sequenceId, ...takenIds.map((id) => id + 1), 1);
    const promoted: CartItem = JSON.parse(JSON.stringify(cartItem));
    promoted.cartItemId = cartItemId;
    promoted.addedBy = CartItemAdditionTypes.human;

    dispatch(cartActions.addItemToCart({ cartItem: promoted }));
    if (quantity > 1) {
      dispatch(
        cartActions.updateQuantity({
          cartItemId: cartItemId.toString(),
          quantity,
        })
      );
    }
    return cartItemId;
  };

/**
 * HITL-1615 — per-line promote: APPEND this line to the order.
 *
 * Promotion MOVES rather than copies, so pressing twice cannot duplicate and an
 * emptied My Cart is a real signal. The agent deletes the AI's stale counterpart
 * themselves — the honest two-step, rather than guessing which line this one was
 * meant to correct.
 *
 * The `ORDER_CHANGED` interception that has to accompany this is NOT dispatched
 * here: it goes through `useAgentInterception`, which is the HITL-1606 gate and
 * is a hook. `usePromoteDraftCart` owns the pair and their ordering.
 */
export const promoteDraftLine =
  (cartItemId: number): AppThunk =>
  (dispatch, getState) => {
    const { cartItems, cartItemsQuantity } = getState().draftCart;
    const item = cartItems[cartItemId];
    if (!item) return;

    dispatch(writeLineToOrder(item, cartItemsQuantity?.[cartItemId] ?? 1));
    dispatch(draftCartActions.deleteCartItem(cartItemId.toString()));

    logger.info({
      message: 'Promoted a My Cart line into the order',
      moreInfo: item.name,
    });
  };

/**
 * HITL-1615 — cart-level promote: REPLACE the order with My Cart.
 *
 * Replace, not append, is what avoids the duplicate trap: an agent who shadowed
 * the whole order would otherwise double every line.
 *
 * Deliberately NOT `clearCart()`. That returns `initialCartState` and would take
 * the coupons, loyalties and POS items with it — none of which this story is
 * replacing, and none of which the agent asked to lose. Only the ITEMS are
 * replaced, one delete at a time.
 *
 * Every dispatch here is synchronous inside one handler, so React commits them
 * as a single render: the cart is never observably empty between the deletes and
 * the writes, and `OrderReview`'s cart effect sees one transition rather than
 * two.
 */
export const replaceOrderWithDraftCart =
  (): AppThunk => (dispatch, getState) => {
    const { cartItems, cartItemsQuantity } = getState().draftCart;
    const draftIds = Object.keys(cartItems ?? {});
    if (!draftIds.length) return;

    const existingIds = Object.keys(getState().cart.cartItems ?? {});
    existingIds.forEach((id) => dispatch(cartActions.deleteCartItem(id)));

    draftIds.forEach((id) => {
      dispatch(writeLineToOrder(cartItems[id], cartItemsQuantity?.[id] ?? 1));
    });

    dispatch(draftCartActions.clearDraftCart());

    logger.info({
      message: 'Replaced the order with My Cart',
      moreInfo: `${existingIds.length} line(s) out, ${draftIds.length} in`,
    });
  };

export default copyCartItemToDraft;
